import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Bot,
  Send,
  User,
  Loader2,
  Sparkles,
  History,
  Plane,
  MapPin,
  Calendar,
  Wallet,
  Compass,
  Sun,
  Snowflake,
  Umbrella,
  Leaf,
  Download,
  Mail,
  FileText,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const AIPlanner = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  // Get current season and suggestions
  const getCurrentSeason = () => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return { name: "Spring", icon: Leaf, color: "text-green-400" };
    if (month >= 5 && month <= 7) return { name: "Summer", icon: Sun, color: "text-yellow-400" };
    if (month >= 8 && month <= 10) return { name: "Autumn", icon: Umbrella, color: "text-orange-400" };
    return { name: "Winter", icon: Snowflake, color: "text-cyan-400" };
  };

  const season = getCurrentSeason();

  // Seasonal destination suggestions
  const getSeasonalDestinations = () => {
    const suggestions = {
      Spring: [
        { name: "Japan", reason: "Cherry blossom season", emoji: "🌸" },
        { name: "Netherlands", reason: "Tulip fields in bloom", emoji: "🌷" },
        { name: "Portugal", reason: "Perfect weather, fewer crowds", emoji: "☀️" },
      ],
      Summer: [
        { name: "Greece", reason: "Island hopping paradise", emoji: "🏝️" },
        { name: "Iceland", reason: "Midnight sun, endless days", emoji: "🌅" },
        { name: "Croatia", reason: "Beautiful coastline", emoji: "⛵" },
      ],
      Autumn: [
        { name: "Canada", reason: "Spectacular fall foliage", emoji: "🍁" },
        { name: "Germany", reason: "Oktoberfest season", emoji: "🍺" },
        { name: "Italy", reason: "Truffle season, wine harvest", emoji: "🍷" },
      ],
      Winter: [
        { name: "Switzerland", reason: "Skiing in the Alps", emoji: "⛷️" },
        { name: "Thailand", reason: "Escape the cold", emoji: "🌴" },
        { name: "Austria", reason: "Christmas markets", emoji: "🎄" },
      ],
    };
    return suggestions[season.name] || suggestions.Summer;
  };

  // Quick action cards
  const quickActions = [
    { 
      icon: Calendar, 
      title: "Plan Weekend Trip",
      prompt: "Help me plan a weekend getaway for 2-3 days. I'm looking for somewhere relaxing within a 3-hour flight.",
      color: "bg-violet-500/20 text-violet-400"
    },
    { 
      icon: Compass, 
      title: "Explore Destinations",
      prompt: `Suggest 5 unique destinations perfect for ${season.name} travel. Include hidden gems and popular spots.`,
      color: "bg-emerald-500/20 text-emerald-400"
    },
    { 
      icon: Wallet, 
      title: "Budget Calculator",
      prompt: "Help me estimate a travel budget. Ask me about my destination, duration, and travel style.",
      color: "bg-amber-500/20 text-amber-400"
    },
    { 
      icon: Plane, 
      title: "Create Itinerary",
      prompt: "I want to create a detailed day-by-day itinerary. Ask me about my destination and preferences.",
      color: "bg-sky-500/20 text-sky-400"
    },
  ];

  useEffect(() => {
    fetchTrips();
    fetchChatHistory();
    // Add simple welcome message
    setMessages([
      {
        role: "ai",
        content: `Hello, ${user?.name?.split(" ")[0] || "Traveler"}! I'm your Explorio AI travel assistant. How can I help you?`,
      },
    ]);
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API}/trips`, {
        withCredentials: true,
      });
      setTrips(response.data);
    } catch (error) {
    }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/ai/history`, {
        withCredentials: true,
      });
      setChatHistory(response.data);
    } catch (error) {
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setShowSuggestions(false); // Hide suggestions after user sends a message
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const tripContext = selectedTrip
        ? {
            name: selectedTrip.name,
            destination: selectedTrip.destination_country,
            start_date: selectedTrip.start_date,
            end_date: selectedTrip.end_date,
          }
        : null;

      const response = await axios.post(
        `${API}/ai/plan`,
        {
          message: userMessage,
          trip_context: tripContext,
        },
        { withCredentials: true }
      );

      setMessages((prev) => [
        ...prev,
        { role: "ai", content: response.data.response },
      ]);
    } catch (error) {
      toast.error("Failed to get AI response");
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: "I'm sorry, I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (prompt) => {
    setInputMessage(prompt);
    setShowSuggestions(false);
  };

  const handleDestinationClick = (destination) => {
    setInputMessage(`Tell me about traveling to ${destination} this ${season.name}. Include best places to visit, local tips, and estimated budget.`);
    setShowSuggestions(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const loadHistoryItem = (item) => {
    setMessages([
      { role: "user", content: item.user_message },
      { role: "ai", content: item.ai_response },
    ]);
    setShowHistory(false);
  };

  // Export to PDF / Print with Explorio branding
  const exportToPDF = () => {
    if (messages.length <= 1) {
      toast.error("No conversation to export");
      return;
    }

    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const userName = user?.name || "Traveler";

    const msgHtml = messages.map((msg) => {
      const isAi = msg.role === "ai";
      const content = msg.content
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/^### (.*$)/gm, '<h3 style="color:#a78bfa;font-size:15px;margin:12px 0 6px;">$1</h3>')
        .replace(/^## (.*$)/gm, '<h2 style="color:#a78bfa;font-size:17px;margin:16px 0 8px;">$1</h2>')
        .replace(/^# (.*$)/gm, '<h1 style="color:#a78bfa;font-size:20px;margin:18px 0 10px;">$1</h1>')
        .replace(/^- (.*$)/gm, '<li style="margin:2px 0;color:#334155;">$1</li>')
        .replace(/(<li.*<\/li>\n?)+/g, '<ul style="padding-left:18px;margin:6px 0;">$&</ul>')
        .replace(/\n/g, "<br>");

      return `<div style="margin:16px 0;padding:16px;border-radius:10px;${
        isAi
          ? "background:#f8fafc;border-left:4px solid #7c3aed;"
          : "background:#f0fdf4;border-left:4px solid #10b981;"
      }">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <div style="width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;${
            isAi ? "background:#7c3aed;" : "background:#10b981;"
          }">${isAi ? "AI" : "You"}</div>
          <span style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;font-weight:600;">${
            isAi ? "Explorio AI" : "Your Question"
          }</span>
        </div>
        <div style="font-size:14px;line-height:1.7;color:#1e293b;">${content}</div>
      </div>`;
    }).join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const htmlContent = `<!DOCTYPE html><html><head>
      <title>Explorio Travel Plan</title>
      <style>
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        body { margin:0; padding:0; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; background:#fff; }
      </style>
    </head><body>
      <div style="max-width:680px;margin:0 auto;padding:32px 24px;">
        <!-- Header -->
        <div style="text-align:center;padding:24px 0 20px;border-bottom:2px solid #7c3aed;margin-bottom:24px;">
          <h1 style="margin:0;font-size:28px;color:#7c3aed;font-weight:800;letter-spacing:-1px;">Explorio</h1>
          <p style="margin:4px 0 0;font-size:12px;color:#94a3b8;text-transform:uppercase;letter-spacing:2px;">Travel Plan</p>
        </div>
        <!-- Meta -->
        <div style="display:flex;justify-content:space-between;font-size:12px;color:#64748b;margin-bottom:20px;">
          <span>Prepared for: <strong style="color:#1e293b;">${userName}</strong></span>
          <span>${today}</span>
        </div>
        <!-- Content -->
        ${msgHtml}
        <!-- Footer -->
        <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center;">
          <p style="font-size:11px;color:#94a3b8;margin:0;">Generated by <strong style="color:#7c3aed;">Explorio</strong> &mdash; Your AI Travel Companion</p>
        </div>
      </div>
    </body></html>`;
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(htmlContent, { WHOLE_DOCUMENT: true, ADD_TAGS: ['html', 'head', 'body', 'style', 'title'] }));
    printWindow.document.close();
    printWindow.print();
  };

  // Send via Resend email
  const exportViaEmail = async () => {
    if (messages.length <= 1) {
      toast.error("No conversation to send");
      return;
    }

    try {
      toast.loading("Sending email...", { id: "email-send" });
      await axios.post(
        `${API}/ai/send-email`,
        { messages, recipient_email: user?.email },
        { withCredentials: true }
      );
      toast.success(`Travel plan sent to ${user?.email}`, { id: "email-send" });
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send email";
      toast.error(detail, { id: "email-send" });
    }
  };

  return (
    <div className="page-container flex flex-col pt-14" data-testid="ai-planner">
      <SEO title="AI Trip Planner" description="Generate personalized trip itineraries with AI. Enter your destination and dates for an instant travel plan." path="/ai-planner" />
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-4 max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold font-heading text-white">AI Planner</h1>
              <p className="text-xs text-zinc-500">Powered by Claude</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 ${season.color}`}>
              <season.icon className="w-3 h-3" />
              <span className="text-xs font-medium">{season.name}</span>
            </div>
            {messages.length > 1 && (
              <>
                <button
                  onClick={exportToPDF}
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  title="Print / Save as PDF"
                  data-testid="export-pdf-button"
                >
                  <FileText className="w-5 h-5 text-zinc-400 hover:text-emerald-400" />
                </button>
                <button
                  onClick={exportViaEmail}
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  title="Email trip plan"
                  data-testid="export-email-button"
                >
                  <Mail className="w-5 h-5 text-zinc-400 hover:text-sky-400" />
                </button>
              </>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              data-testid="history-button"
            >
              <History className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Trip Context Selector */}
        {trips.length > 0 && (
          <div className="max-w-3xl mx-auto">
          <Select
            value={selectedTrip?.trip_id || "none"}
            onValueChange={(value) => {
              if (value === "none") {
                setSelectedTrip(null);
              } else {
                const trip = trips.find((t) => t.trip_id === value);
                setSelectedTrip(trip || null);
              }
            }}
          >
            <SelectTrigger className="glass-input" data-testid="trip-context-select">
              <SelectValue placeholder="Select a trip for context (optional)" />
            </SelectTrigger>
            <SelectContent className="glass-card border-white/10">
              <SelectItem value="none">No trip selected</SelectItem>
              {trips.map((trip) => (
                <SelectItem key={trip.trip_id} value={trip.trip_id}>
                  {trip.name} - {trip.destination_country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          </div>
        )}
      </div>

      {/* Chat History Panel */}
      {showHistory && chatHistory.length > 0 && (
        <div className="absolute top-20 right-4 w-72 glass-card p-4 z-10 max-h-80 overflow-y-auto">
          <h3 className="text-sm font-medium text-white mb-3">Recent Conversations</h3>
          <div className="space-y-2">
            {chatHistory.slice(0, 10).map((item, idx) => (
              <button
                key={idx}
                onClick={() => loadHistoryItem(item)}
                className="w-full text-left p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <p className="text-sm text-zinc-300 truncate">{item.user_message}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {new Date(item.created_at).toLocaleDateString()}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`chat-message ${msg.role}`}
              data-testid={`message-${idx}`}
            >
              <div className="flex items-start gap-2 sm:gap-3">
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === "ai"
                      ? "bg-violet-500/20"
                      : "bg-sky-500/20"
                  }`}
                >
                  {msg.role === "ai" ? (
                    <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
                  ) : (
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-sky-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  {msg.role === "ai" ? (
                    <div className="prose prose-invert prose-sm sm:prose-base max-w-none 
                      prose-headings:text-white prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
                      prose-h1:text-lg sm:prose-h1:text-xl prose-h2:text-base sm:prose-h2:text-lg prose-h3:text-sm sm:prose-h3:text-base
                      prose-p:text-zinc-300 prose-p:text-sm sm:prose-p:text-base prose-p:leading-relaxed prose-p:mb-3
                      prose-strong:text-white prose-strong:font-semibold
                      prose-ul:my-2 prose-ul:pl-4 prose-li:text-zinc-300 prose-li:text-sm sm:prose-li:text-base prose-li:mb-1
                      prose-ol:my-2 prose-ol:pl-4
                      prose-code:bg-slate-800 prose-code:px-1 prose-code:rounded prose-code:text-emerald-400
                      prose-blockquote:border-l-violet-500 prose-blockquote:bg-slate-800/50 prose-blockquote:py-1 prose-blockquote:px-3
                      break-words">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-white text-sm sm:text-base break-words">{msg.content}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="chat-message ai">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Suggested Prompts and Quick Actions */}
      {showSuggestions && messages.length <= 1 && (
        <div className="p-4 border-t border-white/5 space-y-4">
          {/* Quick Actions */}
          <div className="max-w-3xl mx-auto">
            <p className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Quick Actions
            </p>
            <div className="grid grid-cols-2 gap-2">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.prompt)}
                  className="p-3 glass-card hover:border-white/20 transition-all text-left"
                  data-testid={`quick-action-${idx}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${action.color} flex items-center justify-center mb-2`}>
                    <action.icon className="w-4 h-4" />
                  </div>
                  <p className="text-sm font-medium text-white">{action.title}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Seasonal Destinations */}
          <div className="max-w-3xl mx-auto">
            <p className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {season.name} Destinations
            </p>
            <div className="flex flex-wrap gap-2">
              {getSeasonalDestinations().map((dest, idx) => (
                <button
                  key={idx}
                  onClick={() => handleDestinationClick(dest.name)}
                  className="px-3 py-2 glass-card hover:border-white/20 transition-all flex items-center gap-2"
                  data-testid={`seasonal-dest-${idx}`}
                >
                  <span>{dest.emoji}</span>
                  <span className="text-sm text-white">{dest.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-container">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <Textarea
              placeholder="Describe your dream trip... Where do you want to go? What do you want to experience?"
              className="glass-input min-h-[120px] sm:min-h-[140px] max-h-56 resize-none text-base"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              data-testid="ai-input"
              rows={5}
            />
            <Button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="btn-primary h-auto px-4 self-end"
              data-testid="send-button"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIPlanner;
